import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  SearchIcon,
  PlusIcon,
  XIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'

interface DriverLead {
  id: number
  name: string
  phone: string | null
  email: string | null
  source: string
  source_id: string | null
  instagram_username: string | null
  messenger_username: string | null
  driver_type: string
  cdl_status: string
  years_experience: number
  work_authorization: string
  currently_working: string
  status: string
  notes: string | null
  created_at: string
  updated_at: string
}

interface Filters {
  search?: string | null
  status?: string
  driver_type?: string
  cdl_status?: string
  source?: string
  sort_by?: string
  sort_direction?: 'asc' | 'desc'
}

interface Stats {
  total: number
  application_received: number
  phone_screen: number
  background_check: number
  offer_extended: number
  hired: number
  rejected: number
}

interface DriverLeadsIndexProps {
  driverLeads: DriverLead[]
  filters: Filters
  stats: Stats
}

export default function DriverLeadsIndex({ driverLeads, filters, stats }: DriverLeadsIndexProps) {
  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [sortBy, setSortBy] = useState<string | null>(filters.sort_by || null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    filters.sort_direction || null
  )
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editingNoteValue, setEditingNoteValue] = useState('')

  const handleSearch = (search: string) => {
    router.get('/driver_leads', {
      search: search || undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      driver_type: filters.driver_type !== 'all' ? filters.driver_type : undefined,
      source: filters.source !== 'all' ? filters.source : undefined,
      sort_by: sortBy || undefined,
      sort_direction: sortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleSort = (column: string) => {
    let newSortBy: string | null = column
    let newSortDirection: 'asc' | 'desc' | null = 'asc'

    // Three-state cycle: null → asc → desc → null
    if (sortBy === column) {
      if (sortDirection === 'asc') {
        newSortDirection = 'desc'
      } else if (sortDirection === 'desc') {
        // Clear sort
        newSortBy = null
        newSortDirection = null
      }
    }

    setSortBy(newSortBy)
    setSortDirection(newSortDirection)

    router.get('/driver_leads', {
      search: filters.search,
      status: filters.status !== 'all' ? filters.status : undefined,
      driver_type: filters.driver_type !== 'all' ? filters.driver_type : undefined,
      source: filters.source !== 'all' ? filters.source : undefined,
      sort_by: newSortBy || undefined,
      sort_direction: newSortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleStatusChange = (leadId: number, newStatus: string) => {
    const transformedStatus = newStatus.toLowerCase().replace(/ /g, '_')
    router.patch(`/driver_leads/${leadId}/update_status`, {
      status: transformedStatus,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleDriverTypeChange = (leadId: number, newDriverType: string) => {
    router.patch(`/driver_leads/${leadId}/update_driver_type`, {
      driver_type: newDriverType,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleWorkAuthorizationChange = (leadId: number, newWorkAuthorization: string) => {
    router.patch(`/driver_leads/${leadId}/update_work_authorization`, {
      work_authorization: newWorkAuthorization,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleFilterChange = (filterType: string, value: string) => {
    router.get('/driver_leads', {
      search: searchTerm || undefined,
      status: filterType === 'status' ? value : filters.status !== 'all' ? filters.status : undefined,
      driver_type: filterType === 'driver_type' ? value : filters.driver_type !== 'all' ? filters.driver_type : undefined,
      source: filterType === 'source' ? value : filters.source !== 'all' ? filters.source : undefined,
      sort_by: sortBy || undefined,
      sort_direction: sortDirection || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const startEditingNote = (e: React.MouseEvent, lead: DriverLead) => {
    e.stopPropagation()
    setEditingNoteId(lead.id)
    setEditingNoteValue(lead.notes || '')
  }

  const saveNote = (leadId: number) => {
    const noteContent = editingNoteValue.trim()

    router.patch(`/driver_leads/${leadId}`, { driver_lead: { notes: noteContent || null } }, {
      preserveScroll: true,
    })

    // Delay state clearing to allow click event to fully process
    setTimeout(() => {
      setEditingNoteId(null)
      setEditingNoteValue('')
    }, 0)
  }

  const cancelEditingNote = () => {
    setEditingNoteId(null)
    setEditingNoteValue('')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'application_received':
        return 'bg-blue-600 hover:bg-blue-700'
      case 'phone_screen':
        return 'bg-purple-600 hover:bg-purple-700'
      case 'background_check':
        return 'bg-indigo-600 hover:bg-indigo-700'
      case 'offer_extended':
        return 'bg-orange-600 hover:bg-orange-700'
      case 'hired':
        return 'bg-green-600 hover:bg-green-700'
      case 'rejected':
        return 'bg-gray-600 hover:bg-gray-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const SortableHeader = ({ column, children }: { column: string; children: React.ReactNode }) => {
    const isActive = sortBy === column
    const showUpArrow = isActive && sortDirection === 'asc'
    const showDownArrow = isActive && sortDirection === 'desc'

    return (
      <TableHead
        className="cursor-pointer select-none hover:bg-muted/50"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {children}
          {showUpArrow && <ArrowUpIcon className="size-4" />}
          {showDownArrow && <ArrowDownIcon className="size-4" />}
        </div>
      </TableHead>
    )
  }

  const getSocialMediaId = (lead: DriverLead) => {
    // Instagram: show @instagram_username
    if (lead.source === 'Instagram' && lead.instagram_username) {
      return `@${lead.instagram_username}`
    }
    // Facebook Messenger: show @messenger_username
    if (lead.source === 'Facebook Messenger' && lead.messenger_username) {
      return `@${lead.messenger_username}`
    }
    // Other social media sources: show @source_id (as username handle)
    const socialSources = ['Facebook', 'LinkedIn', 'Indeed']
    if (socialSources.includes(lead.source) && lead.source_id) {
      return `@${lead.source_id}`
    }
    // Non-social sources: show —
    return '—'
  }

  const formatPhone = (phone: string | null) => {
    if (!phone) return '—'
    // Remove all non-numeric characters (dashes, spaces, parentheses, etc.)
    return phone.replace(/[^0-9]/g, '')
  }

  const statuses = ['Application Received', 'Phone Screen', 'Background Check', 'Offer Extended', 'Hired', 'Rejected']
  const driverTypes = ['Company', 'Lease-Owner', 'Owner']
  const workAuthorizations = ['US Citizen', 'Green Card', 'Work Visa', 'No Authorization']

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
{/* Header */}
          <div className="flex items-center justify-between px-4 lg:px-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Driver Leads ({stats.total})</h1>
            </div>
            <Button
              size="sm"
              onClick={() => router.visit('/driver_leads/new')}
              data-testid="create-driver-lead-button"
              className="rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white shadow-lg shadow-[#5589ed]/20"
            >
              <PlusIcon className="mr-2 size-4" />
              New Driver Lead
            </Button>
          </div>

          {/* Filters and Table */}
          <div className="px-4 lg:px-6">
            <Card className="w-full min-w-0 rounded-2xl">
              <CardHeader>
                <CardTitle>Filter Driver Leads</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="search">Search by Name, Phone, or ID</Label>
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="search"
                        type="text"
                        placeholder="Search driver leads..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value)
                          handleSearch(e.target.value)
                        }}
                        className="pl-9 pr-9"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm('')
                            handleSearch('')
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <XIcon className="size-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status-filter">Status</Label>
                    <Select
                      value={filters.status || 'all'}
                      onValueChange={(value) => handleFilterChange('status', value)}
                    >
                      <SelectTrigger id="status-filter">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {statuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="driver-type-filter">Driver Type</Label>
                    <Select
                      value={filters.driver_type || 'all'}
                      onValueChange={(value) => handleFilterChange('driver_type', value)}
                    >
                      <SelectTrigger id="driver-type-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {driverTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="source-filter">Source</Label>
                    <Select
                      value={filters.source || 'all'}
                      onValueChange={(value) => handleFilterChange('source', value)}
                    >
                      <SelectTrigger id="source-filter">
                        <SelectValue placeholder="All Sources" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Indeed">Indeed</SelectItem>
                        <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Walk-in">Walk-in</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Table */}
                <div className="rounded-xl border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <SortableHeader column="source">Source</SortableHeader>
                        <SortableHeader column="social_media_id">Social Media ID</SortableHeader>
                        <SortableHeader column="phone">Phone</SortableHeader>
                        <SortableHeader column="name">Name</SortableHeader>
                        <SortableHeader column="status">Status</SortableHeader>
                        <SortableHeader column="driver_type">Driver Type</SortableHeader>
                        <SortableHeader column="work_authorization">Authorization</SortableHeader>
                        <SortableHeader column="notes">Last Note</SortableHeader>
                        <SortableHeader column="updated_at">Last Updated</SortableHeader>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {driverLeads.length > 0 ? (
                        driverLeads.map((lead) => (
                          <TableRow
                            key={lead.id}
                            data-testid={`driver-lead-row-${lead.id}`}
                          >
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/driver_leads/${lead.id}`)
                              }}
                            >
                              {lead.source}
                            </TableCell>
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/driver_leads/${lead.id}`)
                              }}
                            >
                              {getSocialMediaId(lead)}
                            </TableCell>
                            <TableCell
                              className="cursor-pointer hover:underline"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/driver_leads/${lead.id}`)
                              }}
                            >
                              {formatPhone(lead.phone)}
                            </TableCell>
                            <TableCell
                              className="font-medium cursor-pointer hover:underline"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.preventDefault()
                                  return
                                }
                                router.visit(`/driver_leads/${lead.id}`)
                              }}
                            >
                              {lead.name}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Badge
                                    className={`${getStatusColor(lead.status)} rounded-full text-xs cursor-pointer`}
                                    data-testid={`status-badge-${lead.id}`}
                                  >
                                    {formatStatus(lead.status)}
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {statuses.map((status) => (
                                    <DropdownMenuItem
                                      key={status}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleStatusChange(lead.id, status)
                                      }}
                                      data-testid={`status-option-${status.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {status}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Badge
                                    className="bg-gray-600 hover:bg-gray-700 rounded-full text-xs cursor-pointer"
                                    data-testid={`driver-type-badge-${lead.id}`}
                                  >
                                    {lead.driver_type}
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {driverTypes.map((type) => (
                                    <DropdownMenuItem
                                      key={type}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDriverTypeChange(lead.id, type)
                                      }}
                                      data-testid={`driver-type-option-${type.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {type}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Badge
                                    variant="secondary"
                                    className="cursor-pointer"
                                    data-testid={`work-authorization-badge-${lead.id}`}
                                  >
                                    {lead.work_authorization}
                                  </Badge>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {workAuthorizations.map((auth) => (
                                    <DropdownMenuItem
                                      key={auth}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleWorkAuthorizationChange(lead.id, auth)
                                      }}
                                      data-testid={`work-authorization-option-${auth.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {auth}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                            <TableCell
                              className="max-w-xs"
                              onClick={(e) => {
                                if (editingNoteId === lead.id) {
                                  e.stopPropagation()
                                }
                              }}
                            >
                              {editingNoteId === lead.id ? (
                                <Textarea
                                  value={editingNoteValue}
                                  onChange={(e) => setEditingNoteValue(e.target.value)}
                                  onBlur={() => saveNote(lead.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) {
                                      saveNote(lead.id)
                                    } else if (e.key === 'Escape') {
                                      cancelEditingNote()
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  data-testid={`edit-note-textarea-${lead.id}`}
                                  placeholder="Add note..."
                                  className="min-h-[60px] text-sm"
                                  rows={2}
                                />
                              ) : (
                                <div
                                  className="truncate text-sm text-muted-foreground cursor-pointer hover:underline"
                                  onClick={(e) => startEditingNote(e, lead)}
                                  data-testid={`last-note-${lead.id}`}
                                >
                                  {lead.notes || 'Add note...'}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(lead.updated_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} className="h-24 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <p className="text-muted-foreground">
                                {filters.search || filters.status !== 'all' || filters.driver_type !== 'all' || filters.source !== 'all'
                                  ? 'No driver leads found. Try adjusting your filters.'
                                  : 'No driver leads yet. Create your first driver lead to get started.'}
                              </p>
                              {!filters.search && filters.status === 'all' && filters.driver_type === 'all' && filters.source === 'all' && (
                                <Button
                                  onClick={() => router.visit('/driver_leads/new')}
                                  className="mt-4 rounded-lg bg-[#5589ed] hover:bg-[#4478dc] text-white shadow-lg shadow-[#5589ed]/20"
                                >
                                  <PlusIcon className="mr-2 size-4" />
                                  Create Driver Lead
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Footer */}
                {driverLeads.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    Showing 1 to {driverLeads.length} of {stats.total} driver leads
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
